// /app/frontend/src/lib/teamAdminPerms.js
//
// TP-1+ - Centralised team-admin capability helpers.
//
// Each capability has its OWN phase marker so we can roll out
// progressively without touching individual pages. Flipping a marker
// PLUS the company `feature_flags.team_roster_edit_enabled` /
// `team_partial_publish_enabled` flag is the only change required to
// ship the next slice.
//
// View / edit / publish are intentionally separated so a future
// `read_only_team_admin` role (or any other variant) can be added by
// extending `teamAdminCaps` only.
//
// This module does NOT touch billing, membership, plan gates or
// Stripe. The existing `team_admin` role and role-based permissions
// remain the source of truth; these helpers only add finer-grained
// checks for team-scoped capabilities.

const TP_CAPS = {
  // TP-1 -- read-side: view own team's roster + read-only Rota Style.
  view:                true,
  // TP-2 -- inline cell editing (Add assignment, allocation editor,
  // drag-to-cell, single-cell shift create / update / delete).
  rosterCellEdit:      true,
  // TP-RS.team.2 -- Bulk apply shift + Copy from previous week.
  // Both endpoints (POST /shifts/bulk, POST /roster/copy) already
  // enforce team scope server-side for team_admin callers, so the
  // Team Admin can never touch other teams' rows even with this on.
  // Save / Apply weekly templates is a SEPARATE capability below
  // (`rosterTemplates`) because templates are company-scoped and
  // would leak across teams if exposed.
  rosterBulkWrites:    true,
  // TP-RS.team.2 -- Save week as template / Apply template. Remains
  // OFF for Team Admin because `/api/roster-templates` has no
  // team scope on the document yet; a template is currently a
  // company-wide artefact. Revisit only when templates carry a
  // team_id and write paths enforce it.
  rosterTemplates:     false,
  // TP-RS.team.2 -- one-click Use a Rota Style pattern -> draft.
  // Team Admins now apply their OWN team's pattern only; the
  // company-wide profile path is server-gated to Workspace Admin
  // via `_company_admin_only` in /api/drafts/rota/run-from-style.
  // Cross-team application is impossible because the picker shows
  // only the Team Admin's own team profile.
  rosterStyleApply:    true,
  // Future phase: add / edit employees, resend invites.
  employeeEdit:        false,
  // Future phase: shift template create / edit / toggle.
  shiftTemplatesEdit:  false,
  // TP-3 -- team-scoped partial publish.
  publish:             true,
  // TP-RS.team -- create/edit a team-scoped Rota Style profile and
  // its cells. Gated by the company `feature_flags.team_rota_style_enabled`
  // flag; Workspace Admin behaviour is unchanged.
  rotaStyleEdit:       true,
};

function flagOn(billingMe, key) {
  return Boolean(billingMe && billingMe.feature_flags && billingMe.feature_flags[key]);
}

export function isTeamAdmin(user) {
  return user?.role === "team_admin";
}

// TP-MultiTeamTA (Feb 2026) - resolve the full list of team_ids a
// team_admin manages on the FRONTEND. Mirrors backend `_team_admin_scope`:
//   1. user.team_id (legacy primary)
//   2. user.assigned_team_ids (additive list, may be empty)
// De-duplicated. Sub-team expansion happens server-side; the FE only
// needs the explicit list for picker UIs.
// Returns [] for non team_admin users so callers can rely on the
// length to detect "applies to me" branches.
export function teamScopeIds(user) {
  if (!isTeamAdmin(user)) return [];
  const out = [];
  const primary = user?.team_id;
  if (primary) out.push(primary);
  const extra = Array.isArray(user?.assigned_team_ids)
    ? user.assigned_team_ids : [];
  for (const tid of extra) {
    if (tid && !out.includes(tid)) out.push(tid);
  }
  return out;
}

// TP-MultiTeamTA (Feb 2026) - convenience: true when the user manages
// more than one team. Used by the Roster page to decide whether to
// show the per-team picker on Publish / Apply.
export function isMultiTeamAdmin(user) {
  return teamScopeIds(user).length > 1;
}

// Returns a per-capability boolean object for team_admin users; null
// for everyone else (callers fall back to existing role checks).
export function teamAdminCaps(user, billingMe) {
  if (!isTeamAdmin(user)) return null;
  const editFlag = flagOn(billingMe, "team_roster_edit_enabled");
  const publishFlag = flagOn(billingMe, "team_partial_publish_enabled");
  const rotaStyleFlag = flagOn(billingMe, "team_rota_style_enabled");
  return {
    canView:               editFlag                && TP_CAPS.view,
    canEditRosterCells:    editFlag                && TP_CAPS.rosterCellEdit,
    canRosterBulkWrite:    editFlag                && TP_CAPS.rosterBulkWrites,
    // TP-RS.team.2 -- templates kept on a separate cap so they can
    // be flipped on independently once team-scoped templates ship.
    canRosterTemplates:    editFlag                && TP_CAPS.rosterTemplates,
    canUseRotaStyleApply:  editFlag                && TP_CAPS.rosterStyleApply,
    canEditEmployees:      editFlag                && TP_CAPS.employeeEdit,
    canEditShiftTemplates: editFlag                && TP_CAPS.shiftTemplatesEdit,
    canPublish:            publishFlag             && TP_CAPS.publish,
    // TP-RS.team -- team-owned Rota Style edit. Requires its own
    // feature flag plus the per-capability marker so a future
    // rollout can switch off cleanly.
    canEditOwnRotaStyle:   rotaStyleFlag           && TP_CAPS.rotaStyleEdit,
  };
}

// ----- UI gating helpers --------------------------------------------------
// Each defaults to TRUE for non-team_admin users so company_admin /
// super_admin UI is byte-stable across all TP phases.

export function canEditRoster(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canEditRosterCells);
  return true;
}

export function canPublishRoster(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canPublish);
  return true;
}

export function canEditEmployees(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canEditEmployees);
  return true;
}

// Bulk apply, copy-prev-week.
// TP-RS.team.2 -- this no longer includes Save / Apply weekly
// templates. Use `canRosterTemplateAction` for templates so they
// can stay Workspace Admin only until templates carry a team_id.
export function canRosterWriteAction(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canRosterBulkWrite);
  return true;
}

// TP-RS.team.2 -- Save week as template / Apply template. Kept
// separate from `canRosterWriteAction` because the underlying
// `/api/roster-templates` documents are company-scoped today and
// would leak between teams if exposed to Team Admins.
export function canRosterTemplateAction(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canRosterTemplates);
  return true;
}

// One-click "Use a Rota Style pattern".
// TP-RS.team.2 -- Team Admin may apply only their own team's
// profile. The picker filters profiles to user.team_id; the
// company-wide profile path is server-gated to Workspace Admin
// via `_company_admin_only` in POST /api/drafts/rota/run-from-style.
export function canUseRotaStylePattern(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canUseRotaStyleApply);
  return true;
}

export function canEditShiftTemplates(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canEditShiftTemplates);
  return true;
}

// TP-RS.team -- team-owned Rota Style write access. Workspace Admin /
// super_admin keep full edit access (returns TRUE). Team Admin needs
// both the company flag AND the rotaStyleEdit cap marker.
export function canEditOwnRotaStyle(user, billingMe) {
  if (isTeamAdmin(user)) return Boolean(teamAdminCaps(user, billingMe)?.canEditOwnRotaStyle);
  return true;
}

// Friendly disabled label.
export const TEAM_EDIT_NOT_ENABLED_MSG = "Team editing is not enabled yet.";
